import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Tab,
  Tabs,
  Typography,
  DialogActions,
  TextField,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CommentsPanel from "./CommentsPanel";
import ContactedStatusControl from "./ContactedStatusControl";
import LeadAssignmentPanel from "./LeadAssignmentPanel";
import { LEAD_CONTEXTS } from "../helpers/leadContexts";

const getStyle = (type) => {
  if (type === "ACCEPT") {
    return {
      borderLeft: "6px solid #4caf50",
      badgeColor: "success",
      badgeLabel: "Lead",
    };
  }

  if (type === "NOT ACCEPT") {
    return {
      borderLeft: "6px solid #f44336",
      badgeColor: "error",
      badgeLabel: "Discard",
    };
  }

  return {
    borderLeft: "6px solid #fb8c00",
    badgeColor: "warning",
    badgeLabel: "Unsure",
  };
};

const getLeadTypeLabel = (type) => {
  if (type === "ACCEPT") return "Lead";
  if (type === "NOT ACCEPT") return "Discard";
  return "Unsure";
};

const hasMeaningfulValue = (value) =>
  value !== undefined &&
  value !== null &&
  String(value).trim() !== "" &&
  String(value).trim() !== "Unknown";

const getYearFromDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.getFullYear();
  }

  const year = parseInt(String(value).slice(0, 4), 10);
  return Number.isNaN(year) ? null : year;
};

const formatDisplayDate = (value) => {
  if (!hasMeaningfulValue(value)) return "Unknown";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const formatFundingAmount = (value) => {
  if (!hasMeaningfulValue(value)) return "Unknown";

  const numericValue =
    typeof value === "number"
      ? value
      : parseInt(String(value).replace(/[^0-9.-]/g, ""), 10);

  if (Number.isNaN(numericValue)) return value;

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(numericValue);
};

const addDaysToIsoDate = (isoDate, days) => {
  if (!isoDate) return "";

  const [year, month, day] = String(isoDate)
    .split("-")
    .map((part) => parseInt(part, 10));

  if (!year || !month || !day) {
    return isoDate;
  }

  const utcDate = new Date(Date.UTC(year, month - 1, day + days));
  return utcDate.toISOString().slice(0, 10);
};

const splitCommaSeparatedValues = (value) => {
  if (!hasMeaningfulValue(value)) return [];

  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const formatPersonType = (value) => {
  if (!hasMeaningfulValue(value)) return "Unknown";

  return String(value)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const splitRoleLabel = (value) =>
  String(value || "")
    .split(/\s*(?:,|&|\band\b)\s*/i)
    .map((part) => part.trim())
    .filter(Boolean);

const buildPersonTags = (person) => {
  const tags = [];
  const seen = new Set();
  const addTag = (value) => {
    const formatted = formatPersonType(value);
    const comparable = formatted.toLowerCase();
    if (formatted === "Unknown" || seen.has(comparable)) {
      return;
    }
    seen.add(comparable);
    tags.push(formatted);
  };

  const normalizedRelationship = String(person.relationship || "").trim().toLowerCase();
  const relationshipIsGeneric =
    !normalizedRelationship ||
    normalizedRelationship === "unknown" ||
    normalizedRelationship === "employee";

  if (!relationshipIsGeneric) {
    splitRoleLabel(person.relationship).forEach(addTag);
  }

  if (hasMeaningfulValue(person.title)) {
    splitRoleLabel(person.title).forEach(addTag);
  }

  if (tags.length === 0 && hasMeaningfulValue(person.relationship)) {
    addTag(person.relationship);
  }

  if (tags.length === 0 && hasMeaningfulValue(person.type)) {
    addTag(person.type);
  }

  return tags;
};

const getFundingRoundDateLabel = (value) => {
  if (!hasMeaningfulValue(value)) return "Unknown";
  return value;
};

const formatFundingRoundAmount = (amount, currency) => {
  if (amount === undefined || amount === null || Number.isNaN(Number(amount))) {
    return "Unknown";
  }

  const currencyCode =
    typeof currency === "string" && currency.trim() !== ""
      ? currency.trim().toUpperCase()
      : "USD";

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch (error) {
    return `${currencyCode} ${Number(amount).toLocaleString("en-GB")}`;
  }
};

const formatCommentTimestamp = (value) => {
  if (!value) return "Unknown";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-GB");
};

const getLeadTypeFromScopeClassification = (value) => {
  if (value === "INTEREST") return "ACCEPT";
  if (value === "NOT_INTEREST") return "NOT ACCEPT";
  return "FUZZY";
};

const getScopeClassificationLabel = (value) =>
  getLeadTypeLabel(getLeadTypeFromScopeClassification(value));

const getScopeClassificationChipSx = (value) => {
  if (value === "INTEREST") {
    return {
      color: "#166534",
      backgroundColor: "#f0fdf4",
      borderColor: "rgba(34, 197, 94, 0.35)",
    };
  }

  if (value === "NOT_INTEREST") {
    return {
      color: "#b91c1c",
      backgroundColor: "#fef2f2",
      borderColor: "rgba(239, 68, 68, 0.35)",
    };
  }

  return {
    color: "#b45309",
    backgroundColor: "#fff7ed",
    borderColor: "rgba(251, 146, 60, 0.35)",
  };
};

export default function CompanyCard({
  company,
  feedbackData,
  setFeedbackData,
  getApiJson,
  onClassificationChange,
  postJson,
  deleteJson,
  onManualOverrideSaved,
  assignment,
  canManageAssignments = false,
  assignableUsers = [],
  assignableUsersLoading = false,
  onAssignmentsChanged,
  onContactedChanged,
  updatedWindowFrom,
  updatedWindowTo,
  onOpenGlobaldataMatch,
  onOpenCompanyHouseMatch,
}) {
  const [expanded, setExpanded] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [detailsTab, setDetailsTab] = useState("additional");
  const [expandedPersonId, setExpandedPersonId] = useState(null);
  const [pendingOverride, setPendingOverride] = useState(null);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideJustification, setOverrideJustification] = useState("");
  const [justificationError, setJustificationError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [detailExtraction, setDetailExtraction] = useState(null);
  const [detailExtractionError, setDetailExtractionError] = useState("");
  const [detailExtractionLoading, setDetailExtractionLoading] = useState(false);
  const [detailDelta, setDetailDelta] = useState(null);
  const [detailDeltaError, setDetailDeltaError] = useState("");
  const [detailDeltaLoading, setDetailDeltaLoading] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyError, setHistoryError] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreError, setRestoreError] = useState("");
  const [isRestoring, setIsRestoring] = useState(false);
  const [contactedLoading, setContactedLoading] = useState(false);
  const [contactedError, setContactedError] = useState("");
  const companyId = company.id;
  const feedbackEntry = feedbackData[companyId];
  const existing = feedbackEntry || {
    comment: "",
    previousLeadType: null,
    timestamp: null,
  };

  const leadType = company["Lead Type"];
  const originalLeadType =
    existing.previousLeadType ||
    company["Original Lead Type"] ||
    company["Lead Type"];
  const isManualClassification = Boolean(company["Manual Override"]);
  const showManualChangeChip =
    isManualClassification || leadType !== originalLeadType;
  const leadTypeLabel = getLeadTypeLabel(leadType);
  const originalLeadTypeLabel = getLeadTypeLabel(originalLeadType);
  const style = getStyle(leadType);
  const companyName = company["Organization Name"];
  const globaldataMatchName = company["GlobalData Matching Name"] || "";
  const companyHouseMatchName = company["Company House Matching Name"] || "";
  const clientMatchName = company["Salesforce Client Matching Name"] || "";
  const hasGlobaldataMatch = hasMeaningfulValue(globaldataMatchName);
  const hasCompanyHouseMatch = hasMeaningfulValue(companyHouseMatchName);
  const hasClientMatch = hasMeaningfulValue(clientMatchName);
  const clientLifecycleStatus =
    company["Client Lifecycle Status"] || company.clientLifecycleStatus || "";
  const isConvertedClient = clientLifecycleStatus === "CONVERTED_CLIENT";
  const showClientLifecycleChip =
    hasClientMatch ||
    clientLifecycleStatus === "EXISTING_CLIENT" ||
    clientLifecycleStatus === "CONVERTED_CLIENT" ||
    clientLifecycleStatus === "NOT_CLIENT_ANYMORE";
  const clientLifecycleChipLabel =
    clientLifecycleStatus === "CONVERTED_CLIENT"
      ? "Converted +"
      : clientLifecycleStatus === "NOT_CLIENT_ANYMORE"
      ? "Not client anymore"
      : "Existing Client";
  const isArchived = Boolean(company.Archived);
  const hasAssignment = Boolean(assignment?.assignedTo);
  const isContacted = Boolean(company.contacted ?? company.Contacted);

  const toggleContacted = async (event) => {
    event?.stopPropagation?.();
    if (!postJson || !deleteJson || contactedLoading) {
      return;
    }

    setContactedLoading(true);
    setContactedError("");

    try {
      if (isContacted) {
        await deleteJson(
          `/api/leads/lead-context/${LEAD_CONTEXTS.CRUNCHBASE}/company/${encodeURIComponent(companyName)}/contacted`
        );
      } else {
        await postJson(
          `/api/leads/lead-context/${LEAD_CONTEXTS.CRUNCHBASE}/company/${encodeURIComponent(companyName)}/contacted`
        );
      }

      onContactedChanged?.(companyName, !isContacted);
    } catch (error) {
      console.error("Unable to update contacted status:", error);
      setContactedError(error.message || "Unable to update contacted status.");
    } finally {
      setContactedLoading(false);
    }
  };
  const showDeltaTab = company["Prospect Change Type"] === "UPDATED";
  const activeDetailsTab =
    !showDeltaTab && detailsTab === "delta" ? "additional" : detailsTab;
  const classificationVersion = company["Classification Version"] || 0;
  const foundedYear = getYearFromDate(company["Founded"]);
  const lastFundingYear = getYearFromDate(company["Last Funding Date"]);
  const top5Investors = splitCommaSeparatedValues(company["Top 5 Investors"]);
  const leadInvestors = splitCommaSeparatedValues(company["Lead Investors"]);
  const totalFundingAmount = formatFundingAmount(company["Total Funding Amount"]);
  const people = Array.isArray(detailExtraction?.people) ? detailExtraction.people : [];
  const fundingRounds = Array.isArray(detailExtraction?.fundingRounds)
    ? detailExtraction.fundingRounds
    : [];

  const coverageItems = [
    { label: "Founded", value: company["Founded"] },
    { label: "Funding Date", value: company["Last Funding Date"] },
    { label: "Stage", value: company["Investment Stage"] },
    { label: "Revenue", value: company["Estimated Revenue Range"] },
    { label: "Website", value: company["Website"] },
    { label: "Crunchbase", value: company["Crunchbase"] },
  ];

  const coverageCount = coverageItems.filter((item) => hasMeaningfulValue(item.value)).length;
  const coveragePercent = Math.round((coverageCount / coverageItems.length) * 100);

  const loadHistory = async () => {
    if (!getApiJson) return;

    setHistoryLoading(true);
    setHistoryError("");

    try {
      const nextHistory = await getApiJson(
        `/api/lead-details/company/${encodeURIComponent(companyName)}/history`
      );
      setHistoryEntries(Array.isArray(nextHistory) ? nextHistory : []);
    } catch (error) {
      console.error("Unable to load classification history:", error);
      setHistoryError("Unable to load classification history.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const updateFeedback = (updates) => {
    const newData = {
      ...existing,
      ...updates,
      timestamp: new Date().toISOString(),
    };

    setFeedbackData((prev) => ({
      ...prev,
      [companyId]: newData,
    }));
  };

  const selectOverride = (nextType) => {
    setPendingOverride(nextType);
    setSaveError("");
  };

  const requestOverrideSave = () => {
    if (!pendingOverride || pendingOverride === leadType || !postJson) return;

    setOverrideJustification("");
    setJustificationError("");
    setSaveError("");
    setOverrideDialogOpen(true);
  };

  const closeOverrideDialog = () => {
    if (isSaving) return;

    setOverrideDialogOpen(false);
    setOverrideJustification("");
    setJustificationError("");
  };

  const openHistoryDialog = () => {
    setHistoryDialogOpen(true);
    setHistoryError("");
    loadHistory();
  };

  const closeHistoryDialog = () => {
    if (isRestoring) return;

    setHistoryDialogOpen(false);
    setHistoryError("");
  };

  const requestRestoreOriginal = (historyItem) => {
    setRestoreTarget(historyItem);
    setRestoreError("");
    setRestoreDialogOpen(true);
  };

  const closeRestoreDialog = () => {
    if (isRestoring) return;

    setRestoreDialogOpen(false);
    setRestoreTarget(null);
    setRestoreError("");
  };

  const saveOverride = () => {
    if (!pendingOverride || pendingOverride === leadType || !postJson) return;

    const trimmedJustification = overrideJustification.trim();
    if (!trimmedJustification) {
      setJustificationError("Justification is required.");
      return;
    }

    const scopeClassification =
      pendingOverride === "ACCEPT"
        ? "INTEREST"
        : pendingOverride === "NOT ACCEPT"
        ? "NOT_INTEREST"
        : "FUZZY";

    setIsSaving(true);
    setJustificationError("");
    setSaveError("");

    postJson(
      `/api/leads/lead-context/CRUNCHBASE/company/${encodeURIComponent(
        companyName
      )}/manual-override`,
      {
        scopeClassification,
        justification: trimmedJustification,
        expectedClassificationVersion: classificationVersion,
      }
    )
      .then(async () => {
        updateFeedback({
          previousLeadType: leadType,
        });
        onClassificationChange?.(companyName, pendingOverride);
        if (onManualOverrideSaved) {
          await onManualOverrideSaved();
        }
        setPendingOverride(null);
        setOverrideDialogOpen(false);
        setOverrideJustification("");
      })
      .catch((error) => {
        console.error("Manual override save failed:", error);
        if (error.message.includes("409")) {
          setSaveError("This lead was updated by another user. Refresh and try again.");
          return;
        }
        setSaveError("Unable to save manual classification.");
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const restoreOriginalClassification = async () => {
    if (!postJson || !restoreTarget) return;

    setIsRestoring(true);
    setRestoreError("");

    try {
      const restoredLead = await postJson(
        `/api/leads/lead-context/CRUNCHBASE/company/${encodeURIComponent(
          companyName
        )}/restore-original-classification`,
        {
          expectedClassificationVersion: classificationVersion,
        }
      );

      updateFeedback({
        previousLeadType: leadType,
      });
      onClassificationChange?.(
        companyName,
        getLeadTypeFromScopeClassification(restoredLead.scopeClassification)
      );
      if (onManualOverrideSaved) {
        await onManualOverrideSaved();
      }

      setRestoreDialogOpen(false);
      setRestoreTarget(null);
      setHistoryDialogOpen(false);
    } catch (error) {
      console.error("Restore original classification failed:", error);
      if (error.message.includes("409")) {
        setRestoreError("This lead was updated by another user. Refresh and try again.");
        return;
      }
      setRestoreError("Unable to restore the original classification.");
    } finally {
      setIsRestoring(false);
    }
  };

  const handleCardToggle = () => {
    setExpanded((prev) => {
      const nextExpanded = !prev;
      if (!nextExpanded) {
        setPendingOverride(null);
        setShowMoreDetails(false);
        setDetailsTab("additional");
        setExpandedPersonId(null);
        setDetailExtraction(null);
        setDetailExtractionError("");
        setDetailExtractionLoading(false);
        setDetailDelta(null);
        setDetailDeltaError("");
        setDetailDeltaLoading(false);
        setHistoryDialogOpen(false);
        setRestoreDialogOpen(false);
        setRestoreTarget(null);
      }
      return nextExpanded;
    });
  };

  useEffect(() => {
    if (!expanded || !showMoreDetails || !getApiJson) return;

    let isActive = true;

    const run = async () => {
      setDetailExtractionLoading(true);
      setDetailExtractionError("");

      try {
        const nextExtraction = await getApiJson(
          `/api/leads/lead-context/CRUNCHBASE/company/${encodeURIComponent(
            companyName
          )}/extraction`
        );

        if (isActive) {
          setDetailExtraction({
            people: Array.isArray(nextExtraction?.people)
              ? nextExtraction.people.map((person, index) => ({
                  id:
                    person.id ||
                    person.profileUrl ||
                    person.profile_url ||
                    `${companyName}-person-${index}`,
                  name: person.name || person.personName || person.person_name || "Unknown",
                  type: person.type || person.category || "Unknown",
                  title:
                    person.title ||
                    person.explicitTitleJob ||
                    person.explicit_title_job ||
                    "Unknown",
                  relationship:
                    person.relationship ||
                    person.relationshipToCompany ||
                    person.relationship_to_company ||
                    "Unknown",
                  profileUrl:
                    person.profileUrl || person.profile_url || "",
                }))
              : [],
            fundingRounds: Array.isArray(nextExtraction?.fundingRounds)
              ? nextExtraction.fundingRounds.map((round, index) => ({
                  id:
                    round.id ||
                    round.roundName ||
                    round.round_name ||
                    `${companyName}-round-${index}`,
                  round: round.round || round.roundName || round.round_name || "Unknown",
                  date: round.date || round.announcedDate || round.announced_date || "Unknown",
                  amount:
                    typeof round.amount === "string"
                      ? round.amount
                      : formatFundingRoundAmount(round.amount, round.currency),
                  leadInvestors:
                    round.leadInvestors || round.lead_investors || [],
                }))
              : [],
          });
        }
      } catch (error) {
        console.error("Unable to load extraction details:", error);
        if (isActive) {
          setDetailExtractionError("Unable to load people and funding details.");
        }
      } finally {
        if (isActive) {
          setDetailExtractionLoading(false);
        }
      }
    };

    run();

    return () => {
      isActive = false;
    };
  }, [expanded, showMoreDetails, companyName, getApiJson]);

  useEffect(() => {
    if (!expanded || !showMoreDetails || activeDetailsTab !== "delta" || !showDeltaTab || !getApiJson) return;
    if (!updatedWindowFrom || !updatedWindowTo) return;

    let isActive = true;

    const run = async () => {
      setDetailDeltaLoading(true);
      setDetailDeltaError("");

      try {
        const nextDelta = await getApiJson(
          `/api/lead-details/company/${encodeURIComponent(
            companyName
          )}/delta?from=${updatedWindowFrom}&to=${addDaysToIsoDate(updatedWindowTo, 1)}`
        );

        if (isActive) {
          setDetailDelta(nextDelta);
        }
      } catch (error) {
        console.error("Unable to load detail delta:", error);
        if (isActive) {
          setDetailDeltaError("Unable to load company delta for the selected window.");
        }
      } finally {
        if (isActive) {
          setDetailDeltaLoading(false);
        }
      }
    };

    run();

    return () => {
      isActive = false;
    };
  }, [
    activeDetailsTab,
    companyName,
    detailsTab,
    expanded,
    getApiJson,
    showDeltaTab,
    showMoreDetails,
    updatedWindowFrom,
    updatedWindowTo,
  ]);

  useEffect(() => {
    if (showDeltaTab || detailsTab !== "delta") return;

    setDetailsTab("additional");
    setDetailDelta(null);
    setDetailDeltaError("");
    setDetailDeltaLoading(false);
  }, [detailsTab, showDeltaTab]);

  const renderDetailList = (items, emptyLabel = "Unknown") => {
    if (items.length === 0) {
      return (
        <Typography sx={{ fontSize: "0.92rem", color: "#64748b" }}>
          {emptyLabel}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {items.map((item) => (
          <Chip
            key={item}
            label={item}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 999,
              backgroundColor: "#f8fafc",
              borderColor: "rgba(15, 23, 42, 0.12)",
            }}
          />
        ))}
      </Box>
    );
  };

  const renderFundingTimeline = () => {
    if (fundingRounds.length === 0) {
      return (
        <Typography sx={{ fontSize: "0.92rem", color: "#64748b" }}>
          Funding timeline unavailable.
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          border: "1px solid rgba(15, 23, 42, 0.08)",
          borderRadius: 2,
          backgroundColor: "#ffffff",
          p: 1.25,
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Funding Journey</Typography>
        <Box
          sx={{
            display: "grid",
            gap: 1,
            position: "relative",
          }}
        >
          {fundingRounds.map((round, index) => (
            <Box
              key={round.id}
              sx={{
                display: "grid",
                gridTemplateColumns: "20px minmax(0, 1fr)",
                columnGap: 1,
                alignItems: "start",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  justifyItems: "center",
                  pt: 0.35,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "#0f766e",
                    border: "3px solid #ffffff",
                    boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.08)",
                    zIndex: 1,
                  }}
                />
                {index < fundingRounds.length - 1 && (
                  <Box
                    sx={{
                      width: 2,
                      flex: 1,
                      minHeight: 36,
                      backgroundColor: "#cbd5e1",
                      mt: 0.35,
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  pb: index < fundingRounds.length - 1 ? 1.25 : 0,
                  display: "grid",
                  gap: 0.25,
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>
                  {round.round || "Unknown"}
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", color: "#475569" }}>
                  {getFundingRoundDateLabel(round.date)}
                  {" · "}
                  {round.amount || "Unknown"}
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
                  Lead investors: {Array.isArray(round.leadInvestors) && round.leadInvestors.length > 0
                    ? round.leadInvestors.join(", ")
                    : "Unknown"}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderPeopleDetails = () => {
    if (people.length === 0) {
      return (
        <Typography sx={{ fontSize: "0.92rem", color: "#64748b" }}>
          People details unavailable.
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "grid", gap: 1 }}>
        {people.map((person) => {
          const isExpanded = expandedPersonId === person.id;
          const personTags = buildPersonTags(person);

          return (
            <Box
              key={person.id}
              sx={{
                border: "1px solid rgba(15, 23, 42, 0.08)",
                borderRadius: 2,
                backgroundColor: "#ffffff",
                overflow: "hidden",
              }}
            >
              <Button
                variant="text"
                onClick={() =>
                  setExpandedPersonId((current) =>
                    current === person.id ? null : person.id
                  )
                }
                sx={{
                  width: "100%",
                  justifyContent: "space-between",
                  textTransform: "none",
                  color: "#0f172a",
                  px: 1.25,
                  py: 1,
                  fontWeight: 700,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontWeight: 700 }}>{person.name}</Typography>
                  {personTags.map((tag) => (
                    <Chip
                      key={`${person.id}-${tag}`}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        backgroundColor: "#f8fafc",
                        borderColor: "rgba(15, 23, 42, 0.12)",
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>
                  {isExpanded ? "Hide" : "View"}
                </Typography>
              </Button>

              <Collapse in={isExpanded} timeout={{ enter: 200, exit: 150 }} unmountOnExit>
                <Box
                  sx={{
                    px: 1.25,
                    pb: 1.25,
                    display: "grid",
                    gap: 0.65,
                    borderTop: "1px solid rgba(15, 23, 42, 0.08)",
                  }}
                >
                  <Typography sx={{ pt: 1 }}>
                    <strong>Relationship:</strong> {formatPersonType(person.relationship)}
                  </Typography>
                  <Typography>
                    <strong>Profile URL:</strong>{" "}
                    {person.profileUrl ? (
                      <Link href={person.profileUrl} target="_blank">
                        {person.profileUrl}
                      </Link>
                    ) : (
                      "Unknown"
                    )}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderDeltaDetails = () => {
    if (!updatedWindowFrom || !updatedWindowTo) {
      return (
        <Typography sx={{ fontSize: "0.92rem", color: "#64748b" }}>
          Select an update window to inspect the delta for this company.
        </Typography>
      );
    }

    if (detailDeltaLoading) {
      return (
        <Typography sx={{ fontSize: "0.9rem", color: "#475569" }}>
          Loading company delta for the selected window...
        </Typography>
      );
    }

    if (detailDeltaError) {
      return (
        <Typography sx={{ fontSize: "0.9rem", color: "#b91c1c" }}>
          {detailDeltaError}
        </Typography>
      );
    }

    if (!detailDelta) {
      return (
        <Typography sx={{ fontSize: "0.92rem", color: "#64748b" }}>
          Delta unavailable for this company.
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "grid", gap: 1 }}>
        {detailDelta.renderedDelta ? (
          <Box
            sx={{
              border: "1px solid rgba(15, 23, 42, 0.08)",
              borderRadius: 2,
              backgroundColor: "#ffffff",
              p: 1.1,
            }}
          >
            <Box dangerouslySetInnerHTML={{ __html: detailDelta.renderedDelta }} />
          </Box>
        ) : (
          <Box
            sx={{
              border: "1px solid rgba(15, 23, 42, 0.08)",
              borderRadius: 2,
              backgroundColor: "#ffffff",
              p: 1.1,
            }}
          >
            <Typography sx={{ fontSize: "0.92rem", color: "#64748b" }}>
              No included field changes were detected for this company in the selected window.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Card
      variant="outlined"
      sx={{
        marginBottom: 2,
        borderRadius: 3,
        borderLeft: style.borderLeft,
        transition: "all 0.25s ease",
        boxShadow: expanded
          ? "0px 4px 14px rgba(0,0,0,0.18)"
          : "0px 2px 6px rgba(0,0,0,0.1)",
        ":hover": {
          boxShadow: "0px 6px 18px rgba(0,0,0,0.2)",
          transform: "scale(1.01)",
        },
      }}
    >
      <CardActionArea onClick={handleCardToggle}>
        <CardContent sx={{ position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              maxWidth: "70%",
            }}
          >
            {showManualChangeChip && (
              <Chip
                icon={<PersonIcon />}
                label="Manual Change"
                color="info"
                size="small"
                sx={{
                  opacity: expanded ? 1 : 0.9,
                  transition: "opacity 0.2s ease-in-out",
                }}
              />
            )}

            {isArchived && (
              <Chip
                label="Archived"
                size="small"
                sx={{
                  color: "#92400e",
                  backgroundColor: "#fffbeb",
                  border: "1px solid rgba(245, 158, 11, 0.35)",
                  opacity: expanded ? 1 : 0.9,
                  transition: "opacity 0.2s ease-in-out",
                }}
              />
            )}

            {hasAssignment && (
              <Chip
                label="Assigned"
                size="small"
                sx={{
                  color: "#075985",
                  backgroundColor: "#f0f9ff",
                  border: "1px solid rgba(14, 165, 233, 0.28)",
                  opacity: expanded ? 1 : 0.9,
                  transition: "opacity 0.2s ease-in-out",
                }}
              />
            )}

            {isContacted && (
              <Chip
                label="Contacted"
                size="small"
                sx={{
                  color: "#9f1239",
                  backgroundColor: "#fff1f2",
                  border: "1px solid rgba(244, 63, 94, 0.28)",
                  opacity: expanded ? 1 : 0.9,
                  transition: "opacity 0.2s ease-in-out",
                }}
              />
            )}

            {hasGlobaldataMatch && (
              <Chip
                label="GlobalData"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenGlobaldataMatch?.(globaldataMatchName || companyName);
                }}
                sx={{
                  color: "#166534",
                  backgroundColor: "#f0fdf4",
                  border: "1px solid rgba(34, 197, 94, 0.35)",
                  opacity: expanded ? 1 : 0.9,
                  transition: "opacity 0.2s ease-in-out",
                  cursor: "pointer",
                }}
              />
            )}

            {hasCompanyHouseMatch && (
              <Chip
                label="Company House"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenCompanyHouseMatch?.(companyHouseMatchName || companyName);
                }}
                sx={{
                  color: "#0f766e",
                  backgroundColor: "#ecfeff",
                  border: "1px solid rgba(13, 148, 136, 0.35)",
                  opacity: expanded ? 1 : 0.9,
                  transition: "opacity 0.2s ease-in-out",
                  cursor: "pointer",
                }}
              />
            )}

            {showClientLifecycleChip && (
              <Chip
                label={clientLifecycleChipLabel}
                size="small"
                sx={{
                  color:
                    clientLifecycleStatus === "NOT_CLIENT_ANYMORE"
                      ? "#991b1b"
                      : isConvertedClient
                      ? "#075985"
                      : "#7c2d12",
                  backgroundColor:
                    clientLifecycleStatus === "NOT_CLIENT_ANYMORE"
                      ? "#fef2f2"
                      : isConvertedClient
                      ? "#f0f9ff"
                      : "#fff7ed",
                  border:
                    clientLifecycleStatus === "NOT_CLIENT_ANYMORE"
                      ? "1px solid rgba(239, 68, 68, 0.35)"
                      : isConvertedClient
                    ? "1px solid rgba(14, 165, 233, 0.28)"
                    : "1px solid rgba(249, 115, 22, 0.3)",
                  opacity: expanded ? 1 : 0.9,
                  transition: "opacity 0.2s ease-in-out",
                }}
              />
            )}

            <Chip
              label={style.badgeLabel}
              color={style.badgeColor}
              size="small"
              sx={{
                opacity: expanded ? 1 : 0.9,
                transition: "opacity 0.2s ease-in-out",
              }}
            />
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {companyName}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontStyle: "italic",
              marginTop: 1,
              fontSize: "1rem",
            }}
          >
            {company["Reasoning"]}
          </Typography>
        </CardContent>
      </CardActionArea>

      <Collapse in={expanded} timeout={{ enter: 400, exit: 250 }} unmountOnExit>
        <CardContent sx={{ backgroundColor: "#fafafa" }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                lg: "minmax(0, 1.55fr) minmax(320px, 0.95fr)",
              },
              alignItems: "start",
            }}
          >
            <Box>
              <Typography sx={{ mb: 1 }}>
                <strong>Description:</strong> {company["Description"]}
              </Typography>

              <Typography sx={{ mb: 1 }}>
                <strong>Industries:</strong> {company["Industries"]}
              </Typography>

              <Box sx={{ display: "grid", gap: 0.5, mb: 1 }}>
                <Typography>
                  <strong>Headquarters:</strong> {company["Headquarters Location"]}
                </Typography>
                <Typography>
                  <strong>Employees:</strong> {company["Employees"]}
                </Typography>
                <Typography>
                  <strong>Investment Stage:</strong> {company["Investment Stage"]}
                </Typography>
                <Typography>
                  <strong>Last Funding Date:</strong> {formatDisplayDate(company["Last Funding Date"])}
                </Typography>
              </Box>

              <Typography sx={{ mb: 1 }}>
                <strong>Justification:</strong> {company["Reasoning"]}
              </Typography>

              <Typography sx={{ mb: 1 }}>
                <strong>Classification:</strong>
              </Typography>

              {showManualChangeChip && (
                <Typography sx={{ mb: 1, fontSize: "0.9rem", color: "#475569" }}>
                  {leadType !== originalLeadType
                    ? `Manual classification applied. Changed from ${originalLeadTypeLabel} to ${leadTypeLabel}.`
                    : "Manual classification is currently active."}
                </Typography>
              )}

              {pendingOverride && pendingOverride !== leadType && (
                <Typography sx={{ mb: 1, fontSize: "0.9rem", color: "#b45309" }}>
                  Selected change: {getLeadTypeLabel(pendingOverride)}. Click Save to add a justification and apply.
                </Typography>
              )}

              {saveError && (
                <Typography sx={{ mb: 1, fontSize: "0.9rem", color: "#b91c1c" }}>
                  {saveError}
                </Typography>
              )}

              <Box sx={{ display: "flex", gap: 1.25, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                <Button
                  variant={(pendingOverride || leadType) === "ACCEPT" ? "contained" : "outlined"}
                  color="success"
                  onClick={() => selectOverride("ACCEPT")}
                >
                  Lead
                </Button>

                <Button
                  variant={(pendingOverride || leadType) === "NOT ACCEPT" ? "contained" : "outlined"}
                  color="error"
                  onClick={() => selectOverride("NOT ACCEPT")}
                >
                  Discard
                </Button>

                <Button
                  variant={(pendingOverride || leadType) === "FUZZY" ? "contained" : "outlined"}
                  color="warning"
                  onClick={() => selectOverride("FUZZY")}
                >
                  Unsure
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  disabled={!pendingOverride || pendingOverride === leadType || isSaving}
                  onClick={requestOverrideSave}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  {isSaving ? "SAVING..." : "SAVE"}
                </Button>

                <Button
                  variant="outlined"
                  onClick={openHistoryDialog}
                  disabled={historyLoading}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    color: "#475569",
                    borderColor: "rgba(100, 116, 139, 0.35)",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {historyLoading ? "Loading..." : "History"}
                </Button>
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "start" }}>
                <LeadAssignmentPanel
                  companyName={companyName}
                  assignment={assignment}
                  canManageAssignments={canManageAssignments}
                  assignableUsers={assignableUsers}
                  assignableUsersLoading={assignableUsersLoading}
                  postJson={postJson}
                  deleteJson={deleteJson}
                  onAssignmentsChanged={onAssignmentsChanged}
                />
                <ContactedStatusControl
                  contacted={isContacted}
                  loading={contactedLoading}
                  error={contactedError}
                  onToggle={toggleContacted}
                  sx={{ ml: { sm: 1 } }}
                />
              </Box>
            </Box>

            <CommentsPanel
              expanded={expanded}
              leadContext={LEAD_CONTEXTS.CRUNCHBASE}
              organizationName={companyName}
              getApiJson={getApiJson}
              postJson={postJson}
            />
          </Box>

          <Dialog
            open={overrideDialogOpen}
            onClose={closeOverrideDialog}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Confirm Manual Classification</DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 2, mt: 1, color: "#475569" }}>
                You are changing {companyName} from {leadTypeLabel} to{" "}
                {getLeadTypeLabel(pendingOverride)}.
              </Typography>
              <TextField
                label="Justification"
                multiline
                fullWidth
                rows={4}
                required
                autoFocus
                value={overrideJustification}
                onChange={(event) => {
                  setOverrideJustification(event.target.value);
                  if (justificationError) {
                    setJustificationError("");
                  }
                }}
                error={Boolean(justificationError)}
                helperText={justificationError || "Explain why this manual override is needed."}
              />
              {saveError && (
                <Typography sx={{ mt: 1.5, fontSize: "0.9rem", color: "#b91c1c" }}>
                  {saveError}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={closeOverrideDialog} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={saveOverride}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Confirm Save"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={historyDialogOpen}
            onClose={closeHistoryDialog}
            fullWidth
            maxWidth="lg"
          >
            <DialogTitle>Classification History</DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 2, mt: 0.5, color: "#475569" }}>
                Version history for {companyName}. Latest version appears first.
              </Typography>

              {historyError && (
                <Typography sx={{ mb: 2, fontSize: "0.9rem", color: "#b91c1c" }}>
                  {historyError}
                </Typography>
              )}

              <Box sx={{ display: "grid", gap: 1.25, maxHeight: 520, overflowY: "auto", pr: 0.5 }}>
                {historyLoading ? (
                  <Typography sx={{ fontSize: "0.92rem", color: "#64748b" }}>
                    Loading classification history...
                  </Typography>
                ) : historyEntries.length === 0 ? (
                  <Typography sx={{ fontSize: "0.92rem", color: "#64748b" }}>
                    No classification history available.
                  </Typography>
                ) : (
                  historyEntries.map((entry) => {
                    const isActiveEntry = entry.dataState === "ACTIVE";
                    const canRestoreOriginal =
                      entry.classificationVersion === 0 && !isActiveEntry;

                    return (
                      <Box
                        key={`${entry.classificationVersion}-${entry.classifiedAt || "unknown"}`}
                        sx={{
                          border: isActiveEntry
                            ? "1px solid rgba(37, 99, 235, 0.28)"
                            : "1px solid rgba(15, 23, 42, 0.08)",
                          borderRadius: 2,
                          backgroundColor: isActiveEntry ? "#eff6ff" : "#ffffff",
                          p: 1.25,
                          display: "grid",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                            <Typography sx={{ fontWeight: 700 }}>
                              Version {entry.classificationVersion}
                            </Typography>
                            <Chip
                              label={getScopeClassificationLabel(entry.scopeClassification)}
                              size="small"
                              variant="outlined"
                              sx={getScopeClassificationChipSx(entry.scopeClassification)}
                            />
                            <Chip
                              label={entry.dataState || "UNKNOWN"}
                              size="small"
                              variant={isActiveEntry ? "filled" : "outlined"}
                              color={isActiveEntry ? "info" : "default"}
                              sx={{ fontWeight: 700 }}
                            />
                            <Chip
                              label={entry.classificationType || "UNKNOWN"}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 700,
                                color: "#334155",
                                backgroundColor: "#f8fafc",
                                borderColor: "rgba(100, 116, 139, 0.25)",
                              }}
                            />
                          </Box>

                          {canRestoreOriginal && (
                            <Button
                              variant="outlined"
                              onClick={() => requestRestoreOriginal(entry)}
                              sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                color: "#475569",
                                borderColor: "rgba(100, 116, 139, 0.35)",
                              }}
                            >
                              Restore Original
                            </Button>
                          )}
                        </Box>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(2, minmax(0, 1fr))",
                            },
                            gap: 0.75,
                          }}
                        >
                          <Typography sx={{ fontSize: "0.86rem", color: "#475569" }}>
                            <strong>classifiedBy:</strong> {entry.classifiedBy || "Unknown"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.86rem", color: "#475569" }}>
                            <strong>classifiedAt:</strong> {formatCommentTimestamp(entry.classifiedAt)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: 1.5,
                            backgroundColor: "rgba(248, 250, 252, 0.9)",
                            border: "1px solid rgba(15, 23, 42, 0.06)",
                            px: 1,
                            py: 0.9,
                          }}
                        >
                          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#475569", mb: 0.4 }}>
                            Justification
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.92rem",
                              color: "#1e293b",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {entry.justification || "No justification available."}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={closeHistoryDialog} disabled={isRestoring}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={restoreDialogOpen}
            onClose={closeRestoreDialog}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Restore Original Classification</DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 1.5, mt: 0.5, color: "#475569" }}>
                This will create a new active version for {companyName} using the original
                classification from version 0.
              </Typography>
              {restoreTarget && (
                <Box
                  sx={{
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    borderRadius: 2,
                    backgroundColor: "#f8fafc",
                    p: 1.25,
                    display: "grid",
                    gap: 0.75,
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>
                    Version {restoreTarget.classificationVersion}
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem", color: "#475569" }}>
                    <strong>Classification:</strong>{" "}
                    {getScopeClassificationLabel(restoreTarget.scopeClassification)}
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem", color: "#475569" }}>
                    <strong>Current active version:</strong> {classificationVersion}
                  </Typography>
                </Box>
              )}
              {restoreError && (
                <Typography sx={{ mt: 1.5, fontSize: "0.9rem", color: "#b91c1c" }}>
                  {restoreError}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={closeRestoreDialog} disabled={isRestoring}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={restoreOriginalClassification}
                disabled={isRestoring}
              >
                {isRestoring ? "Restoring..." : "Confirm Restore"}
              </Button>
            </DialogActions>
          </Dialog>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="text"
              onClick={() => setShowMoreDetails((prev) => !prev)}
              sx={{ textTransform: "none", px: 0, fontWeight: 700 }}
            >
              {showMoreDetails ? "Hide More Details" : "More Details"}
            </Button>
          </Box>

          <Collapse in={showMoreDetails} timeout={{ enter: 250, exit: 200 }} unmountOnExit>
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: "1px solid rgba(15, 23, 42, 0.08)",
                display: "grid",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                }}
              >
                <Tabs
                  value={activeDetailsTab}
                  onChange={(event, value) => setDetailsTab(value)}
                  variant={showDeltaTab ? "scrollable" : "fullWidth"}
                  scrollButtons={showDeltaTab ? "auto" : false}
                  allowScrollButtonsMobile={showDeltaTab}
                  sx={{
                    px: 1,
                    pt: 1,
                    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.84rem",
                      minHeight: 42,
                    },
                  }}
                >
                  <Tab label="Additional Information" value="additional" />
                  <Tab label="Financial" value="financial" />
                  <Tab label="People" value="people" />
                  {showDeltaTab && <Tab label="Delta" value="delta" />}
                </Tabs>

                <Box sx={{ p: 1.25, display: "grid", gap: 1 }}>
                  {detailExtractionError && (
                    <Typography sx={{ fontSize: "0.9rem", color: "#b91c1c" }}>
                      {detailExtractionError}
                    </Typography>
                  )}

                  {detailExtractionLoading && (activeDetailsTab === "financial" || activeDetailsTab === "people") && (
                    <Typography sx={{ fontSize: "0.9rem", color: "#475569" }}>
                      Loading latest people and funding details...
                    </Typography>
                  )}

                  {activeDetailsTab === "additional" && (
                    <>
                      <Box
                        sx={{
                          border: "1px solid rgba(15, 23, 42, 0.08)",
                          borderRadius: 2,
                          backgroundColor: "#ffffff",
                          p: 1.25,
                          display: "grid",
                          gap: 1,
                        }}
                      >
                        <Typography sx={{ fontWeight: 700 }}>Data Coverage</Typography>
                        <Box
                          sx={{
                            height: 10,
                            borderRadius: 999,
                            backgroundColor: "#e2e8f0",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${coveragePercent}%`,
                              height: "100%",
                              background:
                                "linear-gradient(90deg, #2dd4bf 0%, #0f766e 100%)",
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: "0.85rem", color: "#475569" }}>
                          {coverageCount} of {coverageItems.length} key company fields
                          available
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: 0.75,
                          }}
                        >
                          {coverageItems.map((item) => {
                            const available = hasMeaningfulValue(item.value);
                            return (
                              <Box
                                key={item.label}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  fontSize: "0.84rem",
                                  color: available ? "#0f172a" : "#94a3b8",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    backgroundColor: available ? "#14b8a6" : "#cbd5e1",
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography sx={{ fontSize: "0.84rem" }}>
                                  {item.label}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          border: "1px solid rgba(15, 23, 42, 0.08)",
                          borderRadius: 2,
                          backgroundColor: "#ffffff",
                          p: 1.25,
                          display: "grid",
                          gap: 1,
                        }}
                      >
                        <Typography sx={{ fontWeight: 700 }}>Company Timeline</Typography>
                        {foundedYear && lastFundingYear ? (
                          <>
                            <Box sx={{ position: "relative", pt: 2, pb: 1 }}>
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 10,
                                  left: 0,
                                  right: 0,
                                  height: 4,
                                  borderRadius: 999,
                                  backgroundColor: "#dbeafe",
                                }}
                              />
                              <Box
                                sx={{
                                  position: "relative",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                }}
                              >
                                {[
                                  {
                                    label: "Founded",
                                    year: foundedYear,
                                    color: "#2563eb",
                                  },
                                  {
                                    label: "Last Funding",
                                    year: lastFundingYear,
                                    color: "#0f766e",
                                  },
                                ].map((point) => (
                                  <Box
                                    key={`${point.label}-${point.year}`}
                                    sx={{
                                      display: "grid",
                                      justifyItems: "center",
                                      gap: 0.5,
                                      minWidth: 90,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        backgroundColor: point.color,
                                        border: "3px solid #ffffff",
                                        boxShadow:
                                          "0 0 0 1px rgba(15, 23, 42, 0.08)",
                                        zIndex: 1,
                                      }}
                                    />
                                    <Typography
                                      sx={{ fontSize: "0.82rem", fontWeight: 700 }}
                                    >
                                      {point.label}
                                    </Typography>
                                    <Typography
                                      sx={{ fontSize: "0.82rem", color: "#475569" }}
                                    >
                                      {point.year}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                            <Typography sx={{ fontSize: "0.85rem", color: "#475569" }}>
                              {Math.max(lastFundingYear - foundedYear, 0)} years from
                              founding to latest funding event
                            </Typography>
                          </>
                        ) : (
                          <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
                            Timeline unavailable because one or more dates are missing.
                          </Typography>
                        )}
                      </Box>

                      <Typography>
                        <strong>Founded:</strong> {formatDisplayDate(company["Founded"])}
                      </Typography>
                      <Typography>
                        <strong>Crunchbase Rank:</strong> {company["Crunchbase Rank"]}
                      </Typography>
                      <Typography>
                        <strong>Original Lead Type:</strong> {originalLeadTypeLabel}
                      </Typography>
                      <Typography>
                        <strong>Website:</strong>{" "}
                        <Link href={company["Website"]} target="_blank">
                          {company["Website"]}
                        </Link>
                      </Typography>
                      <Typography>
                        <strong>Crunchbase:</strong>{" "}
                        <Link href={company["Crunchbase"]} target="_blank">
                          {company["Crunchbase"]}
                        </Link>
                      </Typography>
                    </>
                  )}

                  {activeDetailsTab === "financial" && (
                    <>
                      <Box sx={{ display: "grid", gap: 0.75 }}>
                        <Typography>
                          <strong>Total Funding Amount:</strong> {totalFundingAmount}
                        </Typography>
                        <Typography>
                          <strong>Last Funding Date:</strong>{" "}
                          {formatDisplayDate(company["Last Funding Date"])}
                        </Typography>
                        <Typography>
                          <strong>Investment Stage:</strong>{" "}
                          {company["Investment Stage"] || "Unknown"}
                        </Typography>
                        <Typography>
                          <strong>Estimated Revenue Range:</strong>{" "}
                          {company["Estimated Revenue Range"] || "Unknown"}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 1 }}>
                        {renderFundingTimeline()}
                      </Box>
                    </>
                  )}

                  {activeDetailsTab === "people" && (
                    <>
                      <Box sx={{ display: "grid", gap: 1.25 }}>
                        <Box
                          sx={{
                            border: "1px solid rgba(15, 23, 42, 0.08)",
                            borderRadius: 2,
                            backgroundColor: "#ffffff",
                            p: 1.1,
                            display: "grid",
                            gap: 0.75,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>Top 5 Investors</Typography>
                          {renderDetailList(top5Investors)}
                        </Box>

                        <Box
                          sx={{
                            border: "1px solid rgba(15, 23, 42, 0.08)",
                            borderRadius: 2,
                            backgroundColor: "#ffffff",
                            p: 1.1,
                            display: "grid",
                            gap: 0.75,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>Lead Investors</Typography>
                          {renderDetailList(leadInvestors)}
                        </Box>

                        <Box sx={{ display: "grid", gap: 0.75 }}>
                          <Typography sx={{ fontWeight: 700 }}>People</Typography>
                          {renderPeopleDetails()}
                        </Box>
                      </Box>
                    </>
                  )}

                  {showDeltaTab && activeDetailsTab === "delta" && renderDeltaDetails()}
                </Box>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Collapse>

    </Card>
  );
}
